import React, { useEffect, useRef } from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { VideoPreview } from './VideoPreview';
import type { VideoProps } from '@builder4/shared/components/Video';
import {
  parseVideoUrl,
  getVideoThumbnailUrl,
  extractIframeSrc,
} from '@builder4/shared/utils/videoHelpers';
import type { VideoSourceType } from '@builder4/shared/types/video';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useAppDispatch } from '@/store/hooks';
import { showToast } from '@/store/notifications/slice';

export const VIDEO_UPLOAD_TOAST_TYPE = 'video-upload';

// WHY: Map keyed by node id so each video component has its own cancel handler.
// ToastContainer reads the handler for the active upload toast without prop-drilling.
export const videoUploadCancelMap = new Map<string, () => void>();

interface NodeVideoPreviewSettingsProps {
  mode?: VideoSourceType;
}

const NodeVideoPreviewSettings: React.FC<NodeVideoPreviewSettingsProps> = ({ mode = 'url' }) => {
  const { nodeProps, id, displayName } = useNode((node) => ({
    id: node.id,
    nodeProps: node.data.props as VideoProps,
    displayName: node.data.displayName,
  }));

  const { actions } = useEditor();
  const dispatch = useAppDispatch();
  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);

  const url = getBPProp(nodeProps, 'url');
  const embedCode = getBPProp(nodeProps, 'embedCode');
  const thumbnailUrl = getBPProp(nodeProps, 'thumbnailUrl');

  // WHY: Track previous video source to only show toasts when the user actually changed the URL/embed,
  // not on initial mount or when thumbnailUrl triggers a re-run of the effect
  const prevVideoSourceRef = useRef<string | null | undefined>(undefined);

  // WHY: Track raw field values (url/embedCode) separately from parsed videoSource
  // so Cancel restores the original embed markup, not just the extracted src URL.
  const prevRawValueRef = useRef<string | undefined>(undefined);

  // WHY: Suppress effect re-trigger after Cancel to avoid showing a new toast
  // when the reverted value causes url/embedCode deps to change.
  const cancelRevertRef = useRef(false);

  useEffect(() => {
    const videoSource =
      mode === 'url' ? url || null : embedCode ? extractIframeSrc(embedCode) : null;
    const rawValue = mode === 'url' ? url || '' : embedCode || '';

    // WHY: After Cancel reverts the props, the effect re-runs due to dep changes.
    // Skip the toast/fetch logic but keep refs in sync with the reverted state.
    if (cancelRevertRef.current) {
      cancelRevertRef.current = false;
      prevVideoSourceRef.current = videoSource;
      prevRawValueRef.current = rawValue;
      return;
    }

    // WHY: Capture old values before updating refs so Cancel can revert to actual previous source.
    // undefined = initial mount (no toast), null/string = previously seen value (toast if changed).
    const oldSource = prevVideoSourceRef.current;
    const oldRawValue = prevRawValueRef.current;
    const sourceChanged = oldSource !== undefined && oldSource !== videoSource;
    prevVideoSourceRef.current = videoSource;
    prevRawValueRef.current = rawValue;

    if (!videoSource) {
      if (thumbnailUrl) {
        actions.setProp(id, (props: VideoProps) => {
          setBPProp(props, 'thumbnailUrl', undefined);
        });
      }
      return;
    }

    const parsed = parseVideoUrl(videoSource);
    if (!parsed.isValid) {
      if (thumbnailUrl) {
        actions.setProp(id, (props: VideoProps) => {
          setBPProp(props, 'thumbnailUrl', undefined);
        });
      }
      return;
    }

    let cancelled = false;

    // WHY: Show "Uploading video..." only if the fetch takes noticeable time,
    // so fast-resolving thumbnails (e.g. YouTube) skip straight to "Video uploaded."
    const loadingTimerId = sourceChanged
      ? window.setTimeout(() => {
          if (!cancelled) {
            dispatch(
              showToast({
                text: 'Uploading video...',
                state: 'info',
                loading: true,
                autoDismiss: false,
                showCloseButton: false,
                buttonSecondaryText: 'Cancel',
                toastType: VIDEO_UPLOAD_TOAST_TYPE,
              }),
            );
          }
        }, 100)
      : undefined;

    // WHY: Store cancel handler keyed by node id so ToastContainer can revert the video on Cancel click.
    // Uses raw field value (not parsed videoSource) so embed mode restores the original iframe markup.
    if (sourceChanged) {
      videoUploadCancelMap.set(id, () => {
        cancelled = true;
        clearTimeout(loadingTimerId);
        cancelRevertRef.current = true;
        actions.setProp(id, (props: VideoProps) => {
          if (mode === 'url') {
            setBPProp(props, 'url', oldRawValue || '');
          } else {
            setBPProp(props, 'embedCode', oldRawValue || '');
          }
          setBPProp(props, 'thumbnailUrl', undefined);
        });
        videoUploadCancelMap.delete(id);
      });
    }

    getVideoThumbnailUrl(parsed.provider, parsed.videoId)
      .then((thumbnail) => {
        if (cancelled) return;
        clearTimeout(loadingTimerId);
        videoUploadCancelMap.delete(id);
        if (thumbnail !== thumbnailUrl) {
          actions.setProp(id, (props: VideoProps) => {
            setBPProp(props, 'thumbnailUrl', thumbnail ?? undefined);
          });
        }
        // WHY: Null thumbnail means provider doesn't support previews, but video is still valid
        if (sourceChanged) {
          dispatch(
            showToast({
              text: 'Video uploaded.',
              state: 'success',
              icon: 'check',
            }),
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        clearTimeout(loadingTimerId);
        videoUploadCancelMap.delete(id);
        if (sourceChanged) {
          dispatch(
            showToast({
              text: 'Upload failed. Please try again.',
              state: 'error',
              icon: 'error',
            }),
          );
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(loadingTimerId);
      videoUploadCancelMap.delete(id);
    };
  }, [url, embedCode, mode, id, actions, thumbnailUrl, dispatch]);

  const handleClearClick = () => {
    actions.setProp(id, (props: VideoProps) => {
      if (mode === 'url') {
        setBPProp(props, 'url', '');
      } else {
        setBPProp(props, 'embedCode', '');
      }
      setBPProp(props, 'thumbnailUrl', undefined);
    });
  };

  return <VideoPreview thumbnailUrl={thumbnailUrl} onClearClick={handleClearClick} />;
};

export default NodeVideoPreviewSettings;
